<template>
  <div v-if="!scriptLoadFailed" class="google-auth-container">
    <div id="google-login-button" class="google-login-button" />
  </div>
</template>

<script setup lang="ts">
import { useGoogleSignIn } from '~/composables/useGoogleSignIn'
import { useI18n } from '~/composables/useI18n'
import { nextTick, onMounted, ref, watch } from 'vue'

const BUTTON_ID = 'google-login-button'
const { initializeGoogleSignIn, isInitialized, scriptLoadFailed } =
  useGoogleSignIn()

// Render the Google Sign-In button
async function renderGoogleButton() {
  if (!window.google?.accounts?.id) return

  // Wait a tick to ensure DOM is ready
  await nextTick()

  const buttonElement = document.getElementById(BUTTON_ID)
  if (!buttonElement) {
    // Retry after a short delay
    setTimeout(() => {
      void renderGoogleButton()
    }, 100)
    return
  }

  const { i18n } = useI18n()
  const locale = i18n.locale || 'en'

  try {
    window.google.accounts.id.renderButton(buttonElement, {
      type: 'standard',
      theme: 'filled_blue',
      size: 'large',
      width: 247,
      locale: locale.split('-')[0],
      use_fedcm_for_button: true,
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.dev) console.log('Error rendering Google button:', error)
  }
}

// Track if button has been rendered to avoid duplicate renders
const buttonRendered = ref(false)

// Watch for Google Sign-In to be initialized
watch(
  isInitialized,
  (initialized) => {
    if (initialized && !buttonRendered.value) {
      void renderGoogleButton()
      buttonRendered.value = true
    }
  },
  { immediate: true },
)

onMounted(async () => {
  // Ensure Google Sign-In initialization is triggered
  // This won't double-initialize thanks to the composable's protection
  try {
    await initializeGoogleSignIn()
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.dev) console.log('Failed to initialize Google Sign-In:', error)
  }
})
</script>

<style scoped>
.google-auth-container,
.google-login-button {
  width: 100%;
  height: 44px;
}

.google-login-button {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
